import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPatch } from '../../../../utils/api';
import ReviewSubmissionModal from '../modals/ReviewSubmissionModal';

const CopyButton = ({ value, isDarkMode }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`px-2 py-1 rounded-md text-xs transition-colors ${
        isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
      }`}
      aria-label="Copy to clipboard"
    >
      {copied ? 'Copied' : (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      )}
    </button>
  );
};

const LoginDetailsBox = ({ conversation, isDarkMode, isAdminDashboard, user }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveOk, setSaveOk] = useState(false);
  const [confirmingCompletion, setConfirmingCompletion] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [hasSubmittedAnyReview, setHasSubmittedAnyReview] = useState(false);
  const [details, setDetails] = useState({ email: '', password: '' });
  const [hasServerData, setHasServerData] = useState(false);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (!conversation?.order_id) return;
      try {
        const tryEndpoints = isAdminDashboard ? ['/api/orders/'] : ['/api/orders/my', '/api/orders/sales'];
        for (const ep of tryEndpoints) {
          const res = await apiGet(ep);
          if (!res.ok) continue;
          const list = await res.json();
          const found = Array.isArray(list) ? list.find(o => String(o.order_id) === String(conversation.order_id)) : null;
          if (found) { if (!ignore) setOrder(found); break; }
        }
      } catch {}
      try {
        setLoading(true);
        const res = await apiGet(`/api/orders/${conversation.order_id}/login-details`);
        if (res.ok) {
          const data = await res.json();
          if (!ignore && data && (data.email || data.password)) {
            setDetails({ email: data.email || '', password: data.password || '' });
            setHasServerData(true);
          }
        }
      } catch {} finally {
        setLoading(false);
      }

      // Check if this order already has a review by current user to toggle button label
      try {
        if (conversation?.order_id && !ignore) {
          const res = await apiGet(`/api/reviews/order/${encodeURIComponent(conversation.order_id)}`);
          if (res.ok) {
            const data = await res.json();
            const hasAny = Array.isArray(data.reviews) && data.reviews.length > 0;
            setHasSubmittedAnyReview(!!hasAny);
          }
        }
      } catch {}
    };
    load();
    return () => { ignore = true; };
  }, [conversation?.order_id, isAdminDashboard]);

  const normalizedStatus = useMemo(() => {
    const raw = (order?.status || conversation?.status || '').toLowerCase().replace(/[-_ ]/g, '');
    return raw; // 'processing', 'nuwanisuru', 'nuwanis', 'completed', etc.
  }, [order?.status, conversation?.status]);

  const roleVisibility = useMemo(() => {
    // Return which roles may view based on status
    // processing: seller+admin
    // nuwanisuru: admin only
    // nuwanis: buyer+admin
    // completed: buyer+admin
    if (normalizedStatus === 'processing') return { buyer: false, seller: true, admin: true };
    if (normalizedStatus === 'nuwanisuru') return { buyer: false, seller: false, admin: true };
    if (normalizedStatus === 'nuwanis') return { buyer: true, seller: false, admin: true };
    if (normalizedStatus === 'completed' || normalizedStatus === 'complete') return { buyer: true, seller: false, admin: true };
    return { buyer: false, seller: false, admin: false };
  }, [normalizedStatus]);

  // Determine current user role relative to this conversation
  const [userEmail, setUserEmail] = useState('');
  useEffect(() => {
    // Lazy import to avoid circulars
    (async () => {
      try {
        const mod = await import('../../../../store/authStore');
        const useAuthStore = mod.default || mod.useAuthStore || null;
        if (useAuthStore) {
          const { user } = useAuthStore.getState ? useAuthStore.getState() : { user: null };
          setUserEmail(user?.email || '');
        }
      } catch {}
    })();
  }, []);

  const isBuyer = useMemo(() => {
    const buyerEmail = conversation?.buyer?.email?.toLowerCase();
    return buyerEmail && userEmail && buyerEmail === userEmail.toLowerCase();
  }, [conversation?.buyer?.email, userEmail]);

  const isSeller = useMemo(() => {
    const sellerEmail = conversation?.seller?.email?.toLowerCase();
    return sellerEmail && userEmail && sellerEmail === userEmail.toLowerCase();
  }, [conversation?.seller?.email, userEmail]);

  const canView = (roleVisibility.admin && isAdminDashboard) ||
                  (roleVisibility.buyer && isBuyer) ||
                  (roleVisibility.seller && isSeller);

  const canEdit = (roleVisibility.admin && isAdminDashboard) ||
                  (roleVisibility.seller && isSeller);

  if (!canView) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDetails(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!conversation?.order_id) return;
    setSaving(true);
    setSaveOk(false);
    setSaveError('');
    try {
      const res = await apiPost(`/api/orders/${conversation.order_id}/login-details`, details);
      if (res.ok) {
        setHasServerData(true);
        setSaveOk(true);
      } else {
        try {
          const data = await res.json();
          setSaveError(data?.error || 'Failed to save');
        } catch (_) {
          setSaveError('Failed to save');
        }
      }
    } catch (e) {
      setSaveError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmCompletion = async () => {
    if (!conversation?.order_id) return;
    setConfirmingCompletion(true);
    setSaveError('');
    try {
      const res = await apiPatch(`/api/orders/${conversation.order_id}`, { status: 'completed' });
      if (res.ok) {
        // Update local order status
        setOrder(prev => prev ? { ...prev, status: 'completed' } : null);
        // Show success message
        setSaveOk(true);
        setTimeout(() => setSaveOk(false), 3000);
      } else {
        try {
          const data = await res.json();
          setSaveError(data?.error || data?.message || 'Failed to confirm completion');
        } catch (_) {
          setSaveError('Failed to confirm completion');
        }
      }
    } catch (e) {
      setSaveError('Network error');
    } finally {
      setConfirmingCompletion(false);
    }
  };

  return (
    <div className="w-full mt-4">
      <div className={`rounded-2xl shadow-lg p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#1a1a1a] border border-gray-700/50'
          : 'bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className={`font-bold text-lg flex items-center gap-2 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 shadow-md">
              <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 17a2 2 0 100-4 2 2 0 000 4z" strokeWidth="2"/>
                <path d="M6 10V7a6 6 0 1112 0v3" strokeWidth="2"/>
                <rect x="3" y="10" width="18" height="11" rx="2" ry="2" strokeWidth="2"/>
              </svg>
            </div>
            <span>Name Login Details</span>
          </div>
          {loading && (
            <span className={isDarkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>Loading…</span>
          )}
        </div>

        <div className={`rounded-xl p-4 border shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-4 ${
          isDarkMode ? 'bg-[#0f0f0f] border-gray-700/50' : 'bg-white border-gray-100'
        }`}>
          <div className="flex flex-col">
            <label className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email / Username</label>
            <div className="flex items-center gap-2">
              <input
                name="email"
                type="text"
                value={details.email}
                onChange={handleChange}
                disabled={!canEdit}
                className={`flex-1 rounded-lg px-3 py-2 text-sm outline-none border transition-colors ${
                  canEdit 
                    ? (isDarkMode ? 'bg-[#121212] border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400')
                    : (isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600')
                }`}
                placeholder={canEdit ? "Enter email / username" : "No details available"}
                autoComplete="off"
              />
              <CopyButton value={details.email} isDarkMode={isDarkMode} />
            </div>
          </div>

          <div className="flex flex-col">
            <label className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Password</label>
            <div className="flex items-center gap-2">
              <input
                name="password"
                type="text"
                value={details.password}
                onChange={handleChange}
                disabled={!canEdit}
                className={`flex-1 rounded-lg px-3 py-2 text-sm outline-none border transition-colors ${
                  canEdit 
                    ? (isDarkMode ? 'bg-[#121212] border-gray-700 text-gray-200 placeholder-gray-500' : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400')
                    : (isDarkMode ? 'bg-gray-800 border-gray-600 text-gray-400' : 'bg-gray-100 border-gray-300 text-gray-600')
                }`}
                placeholder={canEdit ? "Enter password" : "No details available"}
                autoComplete="off"
              />
              <CopyButton value={details.password} isDarkMode={isDarkMode} />
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                isDarkMode ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-indigo-600 hover:bg-indigo-700 text-white'
              }`}
            >
              {saving ? 'Saving…' : hasServerData ? 'Update Details' : 'Save Details'}
            </button>
            {saveOk && (
              <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Saved successfully.</span>
            )}
            {saveError && (
              <span className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{saveError}</span>
            )}
          </div>
        )}
        
        {!canEdit && isBuyer && (
          <div className="mt-4 flex items-center gap-3">
            {normalizedStatus !== 'completed' && normalizedStatus !== 'complete' ? (
              <button
                type="button"
                onClick={handleConfirmCompletion}
                disabled={confirmingCompletion}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isDarkMode ? 'bg-green-900/50 text-green-200 hover:bg-green-800/50' : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {confirmingCompletion ? 'Confirming...' : 'Completed Transactions'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setShowReviewModal(true)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isDarkMode ? 'bg-blue-900/50 text-blue-200 hover:bg-blue-800/50' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {hasSubmittedAnyReview ? 'Change Review' : 'Submit Review'}
              </button>
            )}
            {saveOk && (
              <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Order completed successfully!</span>
            )}
            {saveError && (
              <span className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{saveError}</span>
            )}
          </div>
        )}
      </div>

      {/* Review Submission Modal */}
      <ReviewSubmissionModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        order={order}
        conversation={conversation}
        user={user}
      />
    </div>
  );
};

export default LoginDetailsBox;


